import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "./AuthContext";
import { isDemoMode } from "../demo/demoMode";
import { DEMO_PROVIDER_ID } from "../demo/demoData";

export type ProviderRef = { id: string; name: string | null };

interface ProviderContextType {
  providers: ProviderRef[];
  currentProviderId: string | null;
  setCurrentProviderId: (id: string | null) => void;
  refreshProviders: () => Promise<void>;
}

const ProviderContext = createContext<ProviderContextType>({} as ProviderContextType);

export const useProvider = () => {
  const ctx = useContext(ProviderContext);
  if (!ctx) throw new Error("useProvider must be used within ProviderProvider");
  return ctx;
};

export const ProviderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [providers, setProviders] = useState<ProviderRef[]>([]);
  const [currentProviderId, setCurrentProviderIdState] = useState<string | null>(null);

  // Seed selection from localStorage immediately on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem("flowstock.selectedProviderId");
      if (saved) {
        setCurrentProviderIdState(saved);
      }
    } catch {}
  }, []);

  const setCurrentProviderId = (id: string | null) => {
    setCurrentProviderIdState(id);
    try {
      if (id) localStorage.setItem("flowstock.selectedProviderId", id);
      else localStorage.removeItem("flowstock.selectedProviderId");
    } catch {}
  };

  const refreshProviders = async () => {
    if (!user) {
      setProviders([]);
      return;
    }

    // Demo mode: provide mock provider without querying
    if (isDemoMode()) {
      const demoProviders: ProviderRef[] = [
        { id: DEMO_PROVIDER_ID, name: 'GoldSneakers EU' },
      ];
      setProviders(demoProviders);
      if (!currentProviderId) setCurrentProviderId(DEMO_PROVIDER_ID);
      return;
    }

    try {
      // Fetch providers accessible to this auth user
      const { data, error } = await supabase
        .from("auth_users_providers")
        .select("provider_id, providers(id, name)")
        .eq("auth_user_id", user.id)
        .order("last_access_at", { ascending: false });

      if (error) {
        console.error("Error fetching providers:", error);
        return;
      }

      const list = (data || [])
        .map((r: any) => ({
          id: r.provider_id,
          name: r.providers?.name || null,
        }))
        .filter((p) => p.id); // Filter out any null provider_ids

      setProviders(list);

      // Initialize selection if not set
      if (!currentProviderId) {
        // Try localStorage first
        let saved: string | null = null;
        try {
          saved = localStorage.getItem("flowstock.selectedProviderId");
        } catch {}
        const initial = saved && list.some((p) => p.id === saved) ? saved : (list[0]?.id ?? null);
        setCurrentProviderId(initial);
      } else if (list.length && !list.some((p) => p.id === currentProviderId)) {
        // Currently selected provider is no longer available
        setCurrentProviderId(list[0].id);
      }
    } catch (err) {
      console.error("Exception in refreshProviders:", err);
    }
  };

  useEffect(() => {
    refreshProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const value = useMemo(
    () => ({ providers, currentProviderId, setCurrentProviderId, refreshProviders }),
    [providers, currentProviderId]
  );

  return <ProviderContext.Provider value={value}>{children}</ProviderContext.Provider>;
};
